import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { type Closing as Entry, Stewardship } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Button, Callout, Unavailable, Waiting } from '@beyond-js/ui/react';
import { Go, Outage, Problem, useReauthenticate } from '@accounts/ui';

/**
 * An organization this person cannot open: not one of theirs, or no longer there, answered in place with
 * the way on to their organizations (D06). `code` is the refusal's, when there was one.
 */
export function Closed({ code }: { code: string | null }) {
	const { text, language } = usePresence();
	return (
		<Unavailable
			kind="access"
			title={text('organization.closed.title')}
			reason={text('organization.closed.reason')}
			owner={text('organization.closed.owner')}
			code={code}
			labels={Unavailable.labels[language]}
			action={<Go to="/organizations" label={text('organization.closed.action')} />}
		/>
	);
}

/**
 * An organization in its grace before deletion, as one of its members finds it (ACC-06): when it is
 * deleted, and for an owner **Cancel deletion**. Anything else the page could not open is `fallback`.
 * The organizations in their grace are the page's one read: when Accounts does not answer it the page
 * says so as its body (`Outage`, D163), never as an organization it cannot open.
 */
export function Closing({ organization, fallback }: { organization: string; fallback: ReactNode }) {
	const { text, date, client, refresh, language } = usePresence();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const reauthenticate = useReauthenticate();
	const [entry, setEntry] = useState<Entry | null>();
	const [unread, setUnread] = useState<Refusal>();
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const [done, setDone] = useState(false);

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(
		() =>
			Refusal.answered(async () => {
				const found = await stewardship.closing();
				setEntry(found.find(item => item.organization === organization) ?? null);
			}, setUnread),
		[organization, stewardship]
	);
	useEffect(() => {
		void read();
	}, [read]);

	async function cancel() {
		setBusy(true);
		setFailure(undefined);
		try {
			try {
				await stewardship.restore(organization);
			} catch (error) {
				if (!(Refusal.is(error) && error.code === 'REAUTHENTICATION_REQUIRED' && (await reauthenticate()))) throw error;
				await stewardship.restore(organization);
			}
			setDone(true);
			await refresh();
		} catch (error) {
			if (!(Refusal.is(error) && error.quiet)) setFailure(error);
			// Nothing came back, so the deletion may be cancelled: standing is read again
			if (Refusal.is(error) && error.unknown) await refresh();
		} finally {
			setBusy(false);
		}
	}

	// First in every form of the page, so it stays the same notice while the page changes around it
	const outage = <Outage whole reads={[{ key: 'closing', title: text('outage.closing'), unread, run: read }]} />;
	// Not read: still waiting, Accounts not answering (the notice alone), or refused, which it cannot open
	if (entry === undefined) return <>{outage}{unread ? (unread.unreachable ? null : fallback) : <Waiting language={language} />}</>;
	if (!entry) return <>{outage}{fallback}</>;
	if (done) {
		return (
			<>
				{outage}
				<Callout tone="success" live title={text('closing.cancelled', { organization: entry.name })} />
			</>
		);
	}
	return (
		<>
			{outage}
			{failure ? <Problem error={failure} /> : null}
			<Callout
				tone="warning"
				title={`${entry.name}: ${text('closing.title')}`}
				body={text('closing.lead', { date: date(entry.purge) })}
				actions={
					entry.role === 'owner' ? (
						<Button variant="secondary" busy={busy} onClick={() => void cancel()}>
							{text('closing.cancel')}
						</Button>
					) : null
				}
			/>
		</>
	);
}
